import { useState, useCallback } from 'react';
import { QueryKey } from 'react-query';

import { TaskStatus } from '../../../../../../../main/tasks/tasksTypes';

import {
  UniversalFilterTaskStatusValue,
  UniversalFilterTaskStatusModes,
  UniversalFilterTaskStatusFilterState,
  UniversalFilterTaskStatusOnFilterStateChange,
  UniversalFilterTaskStatusOnChange
} from '../../UniversalFilterTaskStatus.types';

import { useMountEffect } from '../../../../../../../common/hooks/useMountEffect';
import { useReactQueryState } from '../../../../../../../main/common/reactQuery/hooks/useReactQueryState';

import { useUniversalFilterTaskStatusFilterState } from '../useUniversalFilterTaskStatusFilterState';

import {
  UniversalFilterTemplateModeOption,
  UniversalFilterTemplateOnModeChange
} from '../../../../helpers/UniversalFilterTemplate';

import { filtersKeys } from '../../../../../../../locales/keys';

const defaultMode = UniversalFilterTaskStatusModes.IS_ANY_OF;

const modeOptions: UniversalFilterTemplateModeOption<UniversalFilterTaskStatusModes>[] =
  [
    {
      value: UniversalFilterTaskStatusModes.IS_ANY_OF,
      i18nText: filtersKeys.modes.isAnyOf
    },
    {
      value: UniversalFilterTaskStatusModes.IS_NONE_OF,
      i18nText: filtersKeys.modes.isNoneOf
    }
  ];

interface UniversalFilterTaskStatusOptions {
  filterStateCacheKey: QueryKey;
  filterValue?: UniversalFilterTaskStatusValue;
  onChange: UniversalFilterTaskStatusOnChange;
}

function useUniversalFilterTaskStatus({
  filterStateCacheKey,
  filterValue,
  onChange
}: UniversalFilterTaskStatusOptions) {
  const { value: savedState, setValue: saveState } =
    useReactQueryState<UniversalFilterTaskStatusFilterState>(
      filterStateCacheKey
    );

  const { handleApplyFilterState, handleParseFilterValue } =
    useUniversalFilterTaskStatusFilterState({
      onChange
    });

  const [filterState, setFilterState] =
    useState<UniversalFilterTaskStatusFilterState>(
      savedState ||
        handleParseFilterValue(filterValue) || {
          mode: defaultMode,
          statuses: []
        }
    );

  useMountEffect(() => {
    handleApplyFilterState(filterState);
  });

  const handleFilterStateChange =
    useCallback<UniversalFilterTaskStatusOnFilterStateChange>(
      (state) => {
        setFilterState(state);
        handleApplyFilterState(state);
        saveState(state);
      },
      [handleApplyFilterState, saveState]
    );

  const handleModeChange = useCallback<
    UniversalFilterTemplateOnModeChange<UniversalFilterTaskStatusModes>
  >(
    (mode) => {
      handleFilterStateChange({
        mode,
        statuses: filterState.statuses || []
      });
    },
    [filterState, handleFilterStateChange]
  );

  const handleStatusesChange = useCallback<(statuses: TaskStatus[]) => void>(
    (statuses) => {
      handleFilterStateChange({
        mode: filterState.mode,
        statuses: statuses || []
      });
    },
    [filterState, handleFilterStateChange]
  );

  return {
    modeOptions,
    filterState,
    handleModeChange,
    handleStatusesChange
  };
}

export default useUniversalFilterTaskStatus;
